<template>
  <section
    class="product-page-state"
    :class="[`product-page-state--${type}`, { 'product-page-state--compact': compact }]"
    :role="type === 'error' ? 'alert' : 'status'"
    aria-live="polite"
  >
    <div class="product-page-state__icon" aria-hidden="true">
      <span v-if="type === 'loading'" class="product-page-state__spinner"></span>
      <span v-else-if="type === 'search-empty'" class="product-page-state__search-icon"></span>
      <span v-else-if="type === 'error'" class="product-page-state__error-icon">!</span>
      <span v-else class="product-page-state__bag-icon"></span>
    </div>
    <div class="product-page-state__copy">
      <div class="product-page-state__title">{{ resolvedTitle }}</div>
      <div v-if="resolvedDescription" class="product-page-state__description">
        {{ resolvedDescription }}
      </div>
    </div>
  </section>
</template>

<script>
const STATE_I18N_KEYS = {
  loading: {
    title: 'productState.loading'
  },
  error: {
    title: 'productState.loadFailed'
  },
  empty: {
    title: 'productState.emptyTitle',
    description: 'productState.emptyDescription'
  },
  'search-empty': {
    title: 'productState.searchEmptyTitle',
    description: 'productState.searchEmptyDescription'
  }
};

export default {
  name: 'ProductPageState',
  props: {
    type: {
      type: String,
      default: 'loading',
      validator: value => Object.prototype.hasOwnProperty.call(STATE_I18N_KEYS, value)
    },
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    stateKeys() {
      return STATE_I18N_KEYS[this.type] || STATE_I18N_KEYS.loading;
    },
    resolvedTitle() {
      return this.title || this.$t(this.stateKeys.title);
    },
    resolvedDescription() {
      return this.description || (this.stateKeys.description ? this.$t(this.stateKeys.description) : '');
    }
  }
};
</script>

<style lang="less" scoped>
.product-page-state {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 280px;
  padding: 48px 32px;
  background: #FFFFFF;
  border: 1px solid #E5EAF3;
  border-radius: 20px;
  color: #070D3F;
  text-align: center;

  &--compact {
    flex-direction: row;
    justify-content: flex-start;
    min-height: 0;
    padding: 24px;
    text-align: left;

    .product-page-state__icon {
      width: 72px;
      height: 72px;
      margin: 0 20px 0 0;
    }

    .product-page-state__title {
      font-size: 30px;
      line-height: 38px;
    }

    .product-page-state__description {
      margin-top: 4px;
    }
  }

  &__icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 112px;
    height: 112px;
    margin-bottom: 24px;
    flex: 0 0 auto;
    background: #F8FAFF;
    border-radius: 24px;
    color: #0B45FF;
  }

  &__copy {
    min-width: 0;
  }

  &__title {
    color: #070D3F;
    font-size: 34px;
    font-weight: 600;
    line-height: 44px;
  }

  &__description {
    margin-top: 8px;
    color: #6B7280;
    font-size: 27px;
    line-height: 36px;
  }

  &__spinner {
    box-sizing: border-box;
    width: 42px;
    height: 42px;
    border: 5px solid #CCDAFF;
    border-top-color: #0B45FF;
    border-radius: 50%;
    animation: product-state-spin 0.8s linear infinite;
  }

  &__search-icon {
    position: relative;
    box-sizing: border-box;
    width: 42px;
    height: 42px;
    border: 5px solid #0B45FF;
    border-radius: 50%;

    &::after {
      content: '';
      position: absolute;
      right: -17px;
      bottom: -11px;
      width: 22px;
      height: 5px;
      background: #0B45FF;
      border-radius: 4px;
      transform: rotate(45deg);
      transform-origin: left center;
    }
  }

  &__error-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: #0B45FF;
    border-radius: 50%;
    color: #FFFFFF;
    font-size: 34px;
    font-weight: 700;
    line-height: 1;
  }

  &__bag-icon {
    position: relative;
    box-sizing: border-box;
    width: 50px;
    height: 45px;
    margin-top: 9px;
    border: 5px solid #0B45FF;
    border-radius: 8px;

    &::before {
      content: '';
      position: absolute;
      top: -19px;
      left: 10px;
      box-sizing: border-box;
      width: 20px;
      height: 18px;
      border: 5px solid #0B45FF;
      border-bottom: 0;
      border-radius: 12px 12px 0 0;
    }
  }
}

@keyframes product-state-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .product-page-state__spinner {
    animation-duration: 1.6s;
  }
}
</style>
